import {
  ClearAll,
  ClearAllProps,
  ConfigFilterFacet,
  FiltersSelected,
  FiltersSelectedProps,
  useFilters,
} from "@usereactify/search";
import React, { memo, useCallback, useContext, useMemo } from "react";
import { TypeContext } from "../../context/TypeContext";
import { FiltersMobile } from "./FiltersMobile";
import { FiltersPanel } from "./FiltersPanel";
import { TypeFilterButtons } from "./TypeFilterButtons";
import { SkeletonFilters } from "../Skeleton/SkeletonFilters";

type MyFiltersProps = {
  mobile: boolean;
};

export const Filters: React.FC<MyFiltersProps> = memo(
  ({ mobile }) => {
    const { filters } = useFilters();
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const type = useContext(TypeContext);

    const memoizedFilters = useMemo(() => filters, [filters?.length, filters?.[0]?.id, filters?.[0]?.position]);

    const isTypeFilter = useCallback((filter: ConfigFilterFacet) => filter.id === "type", []);

    const topFilter = useMemo(
      () => memoizedFilters?.find((filter) => isTypeFilter(filter)),
      [memoizedFilters, isTypeFilter]
    );

    const btnTextMobile = type === "search" ? "Filter" : "Filter";

    return (
      <>
        {topFilter && !mobile && <TypeFilterButtons filter={topFilter} />}
        {mobile ? (
          <FiltersMobile filters={filters} isOpen={isOpen} setIsOpen={setIsOpen} btnText={btnTextMobile} />
        ) : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              <ClearAll render={ExampleClearAll} />
              <FiltersSelected render={ExampleFiltersSelected} />
            </div>
            <div className="filter-wrapper filter-wrapper--sidebar">
              {!filters || filters.length === 0 ? (
                <SkeletonFilters />
              ) : (
                filters.map((filter) => <FiltersPanel key={filter.id} isDropdown={true} filter={filter} />)
              )}
            </div>
          </>
        )}
      </>
    );
  },
  (prev, next) => {
    return prev.mobile === next.mobile;
  }
);

export type FiltersProps = {
  defaultView?: boolean;
  setDefaultView?: React.Dispatch<React.SetStateAction<boolean>>;
  isOpen: boolean;
  btnText: string;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  filters?: ConfigFilterFacet[];
};

export type ExampleClearAllProps = React.ComponentProps<NonNullable<ClearAllProps["render"]>>;

export const ExampleClearAll: React.FC<ExampleClearAllProps> = (props) => {
  return (
    <li className="tag selected-tag">
      <button className="btn btn--small js-no-transition" onClick={props.clearAll}>
        {"Clear All"}
      </button>
    </li>
  );
};

type ExampleFiltersSelectedProps = React.ComponentProps<NonNullable<FiltersSelectedProps["render"]>>;

export const ExampleFiltersSelected: React.FC<ExampleFiltersSelectedProps> = (props) => {
  return (
    <>
      {props.selectedFilters.map((selectedFilter) => (
        <li key={selectedFilter.key} className="tag selected-tag">
          <button className="btn btn--small js-no-transition" onClick={() => props.handleRemove(selectedFilter.key)}>
            <svg width="8" height="8" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.94334 8L0 1.05665L1.05665 0L8 6.94334L14.9433 0L16 1.05665L9.05665 8L16 14.9433L14.9433 16L8 9.05665L1.05665 16L0 14.9433L6.94334 8Z"
                fill="#ffffff"
              />
            </svg>
            {selectedFilter.label}:{" "}
            {(Array.isArray(selectedFilter.value) ? selectedFilter.value : [selectedFilter.value]).map(
              (txt, i, row) => (
                <span key={i}>
                  {i > 0 && i + 1 <= row.length && ", "}
                  {selectedFilter.label === "Price" && "$"}
                  {txt}
                  {selectedFilter.label === "Width" && "cm"}
                  {selectedFilter.label === "Height" && "cm"}
                  {selectedFilter.label === "Depth" && "cm"}
                </span>
              )
            )}
          </button>
        </li>
      ))}
    </>
  );
};
